import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, BadgeCheck, Lock, MapPin, RefreshCw, Bookmark, X as XIcon, Handshake } from "lucide-react";
import AppShell from "@/components/AppShell";
import LeadDetailModal from "@/components/LeadDetailModal";
import { api, formatApiError } from "@/lib/api";
import { orgLabel } from "@/lib/constants";
import { toast } from "sonner";

function scoreColor(s) {
  if (s >= 80) return { text: "text-[#059669]", bg: "bg-[#DCFCE7]" };
  if (s >= 60) return { text: "text-[#D97706]", bg: "bg-[#FEF3C7]" };
  return { text: "text-slate-500", bg: "bg-slate-100" };
}

export default function Matches() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => api.get("/matches").then(({ data }) => setData(data)).finally(() => setLoading(false)), []);
  useEffect(() => { load(); }, [load]);

  const matchEvent = (matchId, action) => api.post(`/matches/${matchId}/event`, { action }).catch(() => {});
  const openMatch = (m) => { matchEvent(m.match_id, "viewed"); setSelected({ userId: m.profile_id, match: m }); };
  const [acted, setActed] = useState({});
  const act = async (e, m, action) => {
    e.stopPropagation();
    try {
      await api.post(`/matches/${m.match_id}/event`, { action });
      setActed((p) => ({ ...p, [m.match_id]: action }));
      toast.success(action === "saved" ? "Saved" : action === "dismissed" ? "Dismissed" : "Interest recorded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not record action"); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/matches/generate");
      setData(data);
      toast.success(data.total ? `Found ${data.total} potential matches` : "No matches found yet");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const isFree = data?.plan === "free";
  const limit = data?.limit;
  const locked = isFree && data && data.total > (limit || 0);

  return (
    <AppShell title="AI Matches" subtitle="GPT-powered strategic match recommendations.">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          {data && <span className="font-mono">{data.total} total matches{limit ? ` · showing ${Math.min(data.total, limit)} (Free plan)` : " · unlimited"}</span>}
        </div>
        <button onClick={generate} disabled={generating} data-testid="generate-matches-button"
          className="h-11 px-5 rounded-md bg-[#2563EB] text-white font-medium flex items-center gap-2 hover:bg-[#1D4ED8] transition-colors disabled:opacity-60">
          {generating ? <Loader2 className="animate-spin" size={18} /> : <RefreshCw size={18} />}
          {data?.total ? "Regenerate matches" : "Generate matches"}
        </button>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => <div key={i} className="h-36 rounded-xl border border-slate-200 bg-white animate-pulse" />)}
        </div>
      ) : data && data.matches.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-4">
          {data.matches.map((m, i) => {
            const c = scoreColor(m.score);
            return (
              <motion.div key={m.match_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                role="button" tabIndex={0}
                onClick={() => openMatch(m)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openMatch(m); } }}
                className="rounded-xl border border-slate-200 bg-white p-5 hover:shadow-md hover:border-[#2563EB]/40 transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#2563EB]" data-testid={`match-card-${m.profile_id}`}>
                <div className="flex items-start gap-4">
                  <div className={`h-14 w-14 rounded-xl ${c.bg} flex flex-col items-center justify-center shrink-0`}>
                    <span className={`font-mono text-xl font-bold ${c.text}`}>{m.score}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-heading font-bold text-[#0A0F1C] truncate">{m.org_name}</h3>
                      {m.verified && <BadgeCheck size={16} className="text-[#2563EB] shrink-0" />}
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                      <span className="capitalize">{orgLabel(m.org_type)}</span>
                      {m.location && <><span>·</span><MapPin size={11} /> {m.location}</>}
                    </div>
                    <span className="inline-block mt-2 text-[11px] font-semibold uppercase tracking-wide bg-[#0A0F1C] text-white px-2 py-0.5 rounded">{m.opportunity}</span>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{m.reason}</p>
                <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3" onClick={(e) => e.stopPropagation()}>
                  <button data-testid={`match-save-${m.profile_id}`} onClick={(e) => act(e, m, "saved")}
                    className={`h-7 px-2 rounded border text-xs flex items-center gap-1 ${acted[m.match_id] === "saved" ? "border-[#2563EB] text-[#2563EB]" : "border-slate-200 text-slate-500"}`}><Bookmark size={12} /> Save</button>
                  <button data-testid={`match-interest-${m.profile_id}`} onClick={(e) => act(e, m, "interest")}
                    className={`h-7 px-2 rounded border text-xs flex items-center gap-1 ${acted[m.match_id] === "interest" ? "border-[#059669] text-[#059669]" : "border-slate-200 text-slate-500"}`}><Handshake size={12} /> Interested</button>
                  <button data-testid={`match-dismiss-${m.profile_id}`} onClick={(e) => act(e, m, "dismissed")}
                    className={`h-7 px-2 rounded border text-xs flex items-center gap-1 ${acted[m.match_id] === "dismissed" ? "border-slate-400 text-slate-600" : "border-slate-200 text-slate-500"}`}><XIcon size={12} /> Dismiss</button>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center">
          <Sparkles className="mx-auto text-slate-300" size={36} />
          <p className="mt-3 font-medium text-[#0A0F1C]">No matches yet</p>
          <p className="text-sm text-slate-500 mt-1">Make sure your profile is complete, then generate AI matches.</p>
          <Link to="/profile" className="mt-4 inline-flex h-10 px-4 rounded-md border border-slate-300 text-sm font-medium items-center hover:bg-slate-50">Edit profile</Link>
        </div>
      )}

      {locked && (
        <div className="mt-6 rounded-xl border border-[#2563EB]/30 bg-[#2563EB]/5 p-6 flex items-center gap-4" data-testid="matches-locked-banner">
          <div className="h-11 w-11 rounded-lg bg-[#2563EB] flex items-center justify-center shrink-0">
            <Lock size={18} className="text-white" />
          </div>
          <div className="flex-1">
            <p className="font-medium text-[#0A0F1C]">{data.total - limit} more matches are locked</p>
            <p className="text-sm text-slate-600">The Free plan shows up to {limit} matches. Upgrade to Professional for unlimited AI matches.</p>
          </div>
          <Link to="/pricing" data-testid="matches-upgrade-cta" className="h-11 px-5 rounded-md bg-[#2563EB] text-white font-medium flex items-center hover:bg-[#1D4ED8] transition-colors shrink-0">Upgrade</Link>
        </div>
      )}

      <LeadDetailModal open={!!selected} onClose={() => setSelected(null)}
        userId={selected?.userId} match={selected?.match} />
    </AppShell>
  );
}
